import React from "react";
import * as Icons from "lucide-react";
import MetricCard from "@/components/common/MetricCard";
import GlassCard from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import { bandColor } from "./seoUtils";

/**
 * Operator-first SEO home: one next action, then a short list of pages.
 * Advanced measurement / labs stay off this screen.
 */
export default function SEOOverview({
  data,
  loading,
  onOpenPage,
  onDiscover,
  onAdd,
  onIssues,
  onPages,
  onPublishAllDrafts,
  canPublish,
}) {
  if (loading || !data) {
    return <div className="grid grid-cols-1 gap-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-28 rounded-xl glass animate-pulse" />)}</div>;
  }

  const pages = data.pages_managed || 0;
  const dist = data.distribution || {};
  const analyzed = pages - (dist.not_analyzed || 0);
  const drafts = data.draft_count ?? 0;
  const critical = data.critical_issues || 0;
  const alerts = (data.measurement?.alerts || []).length;

  // Single “what should I do?” pick — no four parallel step cards.
  let next = null;
  if (pages === 0) {
    next = {
      title: "Add your website pages",
      body: "Pull URLs from the live site (or paste one). This builds the list you edit titles and descriptions for.",
      primary: { label: "Find pages on the site", onClick: onDiscover },
      secondary: { label: "Paste one URL", onClick: onAdd },
    };
  } else if (analyzed === 0) {
    next = {
      title: "Check a page",
      body: "Open any page → tap Analyze. That scores the title, description, and common SEO problems.",
      primary: { label: "Open page list", onClick: onPages },
    };
  } else if (critical > 0) {
    next = {
      title: "Fix the important problems",
      body: `${critical} issue${critical === 1 ? "" : "s"} need attention (missing titles, bad descriptions, etc.).`,
      primary: { label: "Show issues", onClick: onIssues },
      secondary: { label: "Edit pages", onClick: onPages },
    };
  } else if (drafts > 0 && canPublish) {
    next = {
      title: "Make your edits live",
      body: `${drafts} page${drafts === 1 ? " has" : "s have"} draft changes that visitors don’t see yet. Publish when you’re happy.`,
      primary: { label: `Publish drafts (${drafts})`, onClick: onPublishAllDrafts },
      secondary: { label: "Review pages first", onClick: onPages },
    };
  } else {
    next = {
      title: "You’re caught up",
      body: "Edit a page anytime to change its Google title or description, then Publish. Use Issues if something looks off.",
      primary: { label: "Edit pages", onClick: onPages },
      secondary: critical === 0 ? { label: "View issues", onClick: onIssues } : null,
    };
  }

  return (
    <div className="space-y-6 w-full max-w-[1600px]">
      <GlassCard className="p-6 space-y-4 border border-cyan-500/20 bg-cyan-500/5">
        <p className="text-[11px] uppercase tracking-wider text-cyan-200/70">Do this next</p>
        <h2 className="text-xl text-white font-medium tracking-tight">{next.title}</h2>
        <p className="text-sm text-white/65 leading-relaxed">{next.body}</p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={next.primary.onClick} className="btn-gradient text-white border-0">
            {next.primary.label}
          </Button>
          {next.secondary && (
            <Button variant="ghost" className="text-white/70" onClick={next.secondary.onClick}>
              {next.secondary.label}
            </Button>
          )}
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button type="button" className="text-left" onClick={onPages}>
          <MetricCard label="Pages" value={pages} icon="FileSearch" accent="#3B82F6" />
        </button>
        <button type="button" className="text-left" onClick={onIssues}>
          <MetricCard label="Problems" value={critical + (data.warnings || 0)} icon="AlertTriangle" accent="#F59E0B" />
        </button>
        <button type="button" className="text-left" onClick={onPages}>
          <MetricCard label="Unpublished" value={drafts} icon="Upload" accent="#8B5CF6" />
        </button>
      </div>

      {alerts > 0 && (
        <GlassCard className="p-4 flex items-center justify-between gap-3">
          <p className="text-sm text-amber-100/90">{alerts} traffic alert{alerts === 1 ? "" : "s"} from Search Console / analytics.</p>
          <Button size="sm" variant="ghost" className="text-amber-100" onClick={onIssues}>Review</Button>
        </GlassCard>
      )}

      <GlassCard className="p-0 overflow-hidden">
        <div className="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h3 className="text-sm font-medium text-white">Your pages</h3>
            <p className="text-[11px] text-white/40">Click a row to edit the Google title &amp; description</p>
          </div>
          <Button size="sm" variant="ghost" className="text-white/60 self-start" onClick={onPages}>See all</Button>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[480px]">
          <thead className="text-[11px] uppercase tracking-wider text-white/40">
            <tr>
              <th className="px-5 py-2 text-left">Page</th>
              <th className="px-5 py-2 text-left">Score</th>
              <th className="px-5 py-2 text-left">Live?</th>
            </tr>
          </thead>
          <tbody>
            {(data.top_pages || []).slice(0, 8).map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer" onClick={() => onOpenPage(p)}>
                <td className="px-5 py-3">
                  <div className="text-white">{p.name}</div>
                  <div className="font-mono text-[11px] text-white/40">{p.path}</div>
                </td>
                <td className="px-5 py-3" style={{ color: bandColor(p.seo_score) }}>{p.seo_score ?? "—"}</td>
                <td className="px-5 py-3 text-white/55">
                  {(p.publish_state || p.status) === "published" ? "Yes" : "Draft"}
                </td>
              </tr>
            ))}
            {(data.top_pages || []).length === 0 && (
              <tr>
                <td colSpan={3} className="px-5 py-10 text-center text-white/45 text-sm">
                  No pages yet.{" "}
                  <button type="button" className="text-cyan-300" onClick={onDiscover}>Find pages</button>
                  {" "}or{" "}
                  <button type="button" className="text-cyan-300" onClick={onAdd}>add a URL</button>.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </GlassCard>

      <p className="text-xs text-white/35 leading-relaxed">
        <Icons.Info className="w-3.5 h-3.5 inline mr-1 opacity-70" />
        Saving a draft does not change the live site. Use <strong className="text-white/50 font-medium">Publish</strong> on the page when you want Google (and visitors) to see it.
      </p>
    </div>
  );
}
